import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2 } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { PRIMARY_ROLES } from '@/data/audiences';

interface Props {
  /** `hero` = glass cards on the dark hero. `light` = compact strip at the top of landing pages. */
  variant?: 'hero' | 'light';
  className?: string;
}

/**
 * "WHERE DO I BELONG?" — answered in the first screen.
 *
 * Three large, labelled doors (Homeowner / Contractor / Subcontractor). Each is
 * ONE click into the real workflow; signed-in visitors skip signup and land on
 * the action itself. On phones they render as full-width rows (~64px each) so
 * all three sit above the fold under the headline.
 */
const RoleQuickStart: React.FC<Props> = ({ variant = 'hero', className = '' }) => {
  const { path, lang } = useI18n();
  const { profile } = useAuth();
  const L = (v: { en: string; es: string }) => (lang === 'es' ? v.es : v.en);
  const hero = variant === 'hero';

  return (
    <nav aria-label={lang === 'es' ? 'Elija su rol' : 'Choose your role'} className={className}>
      <p className={`mb-2 text-xs font-bold uppercase tracking-wider ${hero ? 'text-orange-200' : 'text-slate-500'}`}>
        {lang === 'es' ? '¿Quién es usted? Empiece en un clic' : 'Who are you? Start in one click'}
      </p>
      <ul className="grid gap-2.5 sm:grid-cols-3 sm:gap-4">
        {PRIMARY_ROLES.map((r) => {
          const to = path(profile ? r.memberTo : r.guestTo);
          return (
            <li key={r.key}>
              <Link
                to={to}
                data-role-entry={r.key}
                className={`group flex h-full items-center gap-3 rounded-2xl p-3 transition-all duration-200 hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 sm:flex-col sm:items-start sm:gap-2 sm:p-5 ${
                  hero
                    ? 'border border-white/15 bg-white/95 shadow-lg hover:bg-white hover:shadow-xl'
                    : 'border border-slate-200 bg-white shadow-sm hover:border-slate-300 hover:shadow-card'
                }`}
              >
                <span
                  className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm sm:h-12 sm:w-12 ${r.tone}`}
                >
                  <r.icon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-bold uppercase tracking-wide text-slate-500 sm:text-[13px]">
                    {L(r.title)}
                  </span>
                  <span className="mt-0.5 block text-base font-extrabold leading-snug text-slate-900 sm:text-lg">
                    {L(r.action)}
                  </span>
                  <span className="mt-1 hidden text-xs leading-relaxed text-slate-500 sm:block">{L(r.hint)}</span>
                </span>
                <span
                  className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-white transition-transform group-hover:translate-x-1 sm:mt-1 sm:h-auto sm:w-auto sm:gap-1.5 sm:rounded-lg sm:px-3 sm:py-2 sm:text-sm sm:font-bold ${r.tone}`}
                >
                  <span className="hidden sm:inline">{lang === 'es' ? 'Empezar' : 'Start'}</span>
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
      <p className={`mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-semibold ${hero ? 'text-slate-300' : 'text-slate-500'}`}>
        <Link
          to={path(profile ? '/dashboard' : '/register?role=homeowner&type=commercial')}
          className="inline-flex items-center gap-1 underline-offset-2 hover:underline"
        >
          <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
          {lang === 'es' ? 'Propiedad comercial: publicar proyecto' : 'Commercial property? Post a project'}
        </Link>
        {!profile && (
          <Link to={path('/login')} className="underline-offset-2 hover:underline">
            {lang === 'es' ? '¿Ya tiene cuenta? Inicie sesión' : 'Already a member? Sign in'}
          </Link>
        )}
      </p>
    </nav>
  );
};

export default RoleQuickStart;
